import React, { useMemo } from 'react';

import { IPositionWithCandidates } from '@types';
import { formatSkillLabel } from '@utils';
import { SearchField } from 'components/common/SearchField/SearchField';

type ISuggestionKind = 'Candidates' | 'Positions' | 'Tech stack' | 'Location';

interface ISuggestion {
  kind: ISuggestionKind;
  value: string;
}

const MAX_PER_KIND = 4;

const buildSuggestions = (
  positions: IPositionWithCandidates[],
  query: string,
): ISuggestion[] => {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];

  const seen = new Set<string>();
  const byKind: Record<ISuggestionKind, ISuggestion[]> = {
    Candidates: [],
    Positions: [],
    'Tech stack': [],
    Location: [],
  };

  const push = (kind: ISuggestionKind, value?: string | null) => {
    if (!value) return;
    const key = `${kind}:${value.toLowerCase()}`;
    if (seen.has(key)) return;
    if (!value.toLowerCase().includes(needle)) return;
    if (byKind[kind].length >= MAX_PER_KIND) return;
    seen.add(key);
    byKind[kind].push({ kind, value });
  };

  positions.forEach((position) => {
    push('Positions', position.name);
    position.candidates.forEach((candidate) => {
      push('Candidates', `${candidate.firstName} ${candidate.lastName}`);
      push('Location', candidate.country);
      candidate.skills?.forEach((skill) =>
        push('Tech stack', formatSkillLabel(skill)),
      );
    });
  });

  return [
    ...byKind.Candidates,
    ...byKind.Positions,
    ...byKind['Tech stack'],
    ...byKind.Location,
  ];
};

interface ICandidateSearchProps {
  data: IPositionWithCandidates[];
  value: string;
  fullWidth?: boolean;
  onChange: (value: string) => void;
}

export const CandidateSearch: React.FC<ICandidateSearchProps> = ({
  data,
  value,
  fullWidth,
  onChange,
}) => {
  const options = useMemo(() => buildSuggestions(data, value), [data, value]);

  return (
    <SearchField
      value={value}
      placeholder="Search candidates, positions, stack, location"
      fullWidth={fullWidth}
      suggestions={options}
      onChange={onChange}
    />
  );
};
